/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState , useEffect} from "react";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";

import { useDispatch, useSelector } from "react-redux";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import { useLocation, useNavigate } from "react-router-dom";
import { setAuthorizations, setSessionId, setUser } from "../../../features/login/loginSlice";
import { WHOLSTER_REST_BASE_URL } from "services/config";

function Basic() {
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const location = useLocation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sessionId = useSelector((state) => state.login.sessionId);

  useEffect(() => {
    //Persist
    const storedSessionId = localStorage.getItem("sessionId");
    if (storedSessionId) {
      dispatch(setSessionId(storedSessionId));
      // You can also retrieve the user info and authorization data from localStorage if needed
      const storedUser = JSON.parse(localStorage.getItem("user"));
      const storedAuths = JSON.parse(localStorage.getItem("authorizations"));
      if (storedUser) dispatch(setUser(storedUser));
      if (storedAuths) dispatch(setAuthorizations(storedAuths));
    }
  }, [dispatch]);


  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const handlePasswordChange = (e) => setPassword(e.target.value);

  const handleUsernameChange = (e) => setUsername(e.target.value);

  if (sessionId && location?.state?.location?.pathname) {
    navigate(location?.state?.location?.pathname);
  }

  const onLoginAttempt = async () => {
    const response = await fetch(`${WHOLSTER_REST_BASE_URL}/user/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        username,
        password,
      }),
    });

    if (!response.ok) {
      // Handle unsuccessful login (e.g., display error message)
      setLoginError(true)
      throw new Error("Login failed. Please check your credentials.");
    }
    const data = await response.json();
    dispatch(setAuthorizations(data.authorizations));
    dispatch(setUser(data.user));
    dispatch(setSessionId(data.sessionId));

    // Store sessionId, user data, and authorizations in localStorage
    localStorage.setItem("sessionId", data.sessionId);
    localStorage.setItem("user", JSON.stringify(data.user));
    localStorage.setItem("authorizations", JSON.stringify(data.authorizations));

    if (location?.state?.location?.pathname) {
      navigate(location.state.location.pathname);
    } else {
      navigate("/dashboard"); // Redirect to the default page (e.g., Dashboard)
    }

  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onLoginAttempt();
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign in
          </MDTypography>
          { loginError && <MDTypography variant="h4" fontWeight="medium" color="red" mt={1}>
            Username or Password is incorrect
          </MDTypography> }
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                value={username}
                onChange={handleUsernameChange}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Password"
                value={password}
                onKeyDown={handleKeyDown}
                onChange={handlePasswordChange}
                fullWidth
              />
            </MDBox>
            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Remember me
              </MDTypography>
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton variant="gradient" color="info" fullWidth onClick={() => onLoginAttempt()}>
                sign in
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
